import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "antd";
import {
   CompassOutlined,
   CustomerServiceOutlined,
   ExportOutlined,
   NotificationOutlined,
   PlayCircleOutlined,
   ReadOutlined,
   RightOutlined,
   SearchOutlined,
   VideoCameraOutlined
} from "@ant-design/icons";
import { LIVE_TRAINING_URL } from "./helpCenterConfig";
import { searchItems, sortByRelevance } from "./helpSearch";
import { articleSearchFields } from "./guideHelpers";
import { videoSearchFields } from "./helpVideos";
import { ArticleLink } from "./UserGuide";
import { VideoCard, VideoPlayer } from "./VideoLibrary";
import SparkleIcon from "../../pages/workspace/components/survey/components/aiChat/SparkleIcon";

const HelpItem = ({ icon, title, description, meta, highlighted, ...props }) => {
   const Element = props.to ? Link : props.href ? "a" : "button";

   return (
      <Element
         className={`help-center__item${highlighted ? " help-center__item--highlighted" : ""}`}
         {...(Element === "button" ? { type: "button" } : {})}
         {...props}
      >
         <span className="help-center__item-icon">{icon}</span>
         <span className="help-center__item-text">
            <span className="help-center__item-title">{title}</span>
            <span className="help-center__item-description">{description}</span>
         </span>
         <span className="help-center__item-meta">{meta}</span>
      </Element>
   );
};

const SearchResults = ({ query, articles, videos, onOpenArticle, onPlay, onAsk }) => {
   // Title matches first: in the guide's own order, "Read the project
   // dashboard" comes before "Publish and share your survey" for "publish survey".
   const articleResults = useMemo(
      () =>
         sortByRelevance(
            searchItems(articles, query, articleSearchFields),
            query,
            articleSearchFields
         ),
      [articles, query]
   );
   const videoResults = useMemo(
      () =>
         sortByRelevance(searchItems(videos, query, videoSearchFields), query, videoSearchFields),
      [videos, query]
   );
   const question = query.trim();

   return (
      <>
         {articleResults.length > 0 && (
            <section className="help-center__section">
               <h5 className="help-center__section-title">Articles</h5>
               <div className="help-center__articles">
                  {articleResults.map((article) => (
                     <ArticleLink key={article.id} article={article} onOpen={onOpenArticle} />
                  ))}
               </div>
            </section>
         )}

         {videoResults.length > 0 && (
            <section className="help-center__section">
               <h5 className="help-center__section-title">Videos</h5>
               <div className="help-center__videos">
                  {videoResults.map((video) => (
                     <VideoCard key={video.id} video={video} onPlay={onPlay} />
                  ))}
               </div>
            </section>
         )}

         {articleResults.length === 0 && videoResults.length === 0 && (
            <p className="help-center__no-results">No articles or videos match “{question}”.</p>
         )}

         <button type="button" className="help-center__ask-cta" onClick={() => onAsk(question)}>
            <SparkleIcon size={18} />
            <span className="help-center__ask-cta-text">
               Ask AI: <strong>“{question}”</strong>
            </span>
            <RightOutlined />
         </button>
      </>
   );
};

/**
 * The Help Center's first screen: one search over articles and videos,
 * suggestions for the current page, then everything else the Help Center
 * offers.
 */
const HelpHome = ({
   searchRef,
   articles,
   videos,
   pageArticles,
   updateCount,
   recentTickets,
   canOpenTickets,
   onPush,
   onOpenArticle,
   onOpenWhatsNew,
   onAsk,
   onClose
}) => {
   const [query, setQuery] = useState("");
   const [playing, setPlaying] = useState(null);
   const hasVideos = videos.length > 0;

   return (
      <>
         <Input
            ref={searchRef}
            allowClear
            className="help-center__search"
            placeholder="Search for help"
            aria-label="Search articles and videos"
            prefix={<SearchOutlined />}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
         />

         {query.trim() ? (
            <SearchResults
               query={query}
               articles={articles}
               videos={videos}
               onOpenArticle={onOpenArticle}
               onPlay={setPlaying}
               onAsk={onAsk}
            />
         ) : (
            <>
               {pageArticles.length > 0 && (
                  <section className="help-center__section">
                     <h5 className="help-center__section-title">Suggested for this page</h5>
                     <div className="help-center__articles">
                        {pageArticles.map((article) => (
                           <ArticleLink key={article.id} article={article} onOpen={onOpenArticle} />
                        ))}
                     </div>
                  </section>
               )}

               <section className="help-center__section">
                  <h5 className="help-center__section-title">Learn</h5>
                  {updateCount > 0 && (
                     <HelpItem
                        icon={<NotificationOutlined />}
                        title="What's new"
                        description="The latest features and improvements"
                        meta={<RightOutlined />}
                        onClick={onOpenWhatsNew}
                     />
                  )}
                  {/* Listed before the first video is published, like Product
                      Tour, so users know it is coming; it opens once there is
                      something to play. */}
                  <HelpItem
                     icon={<PlayCircleOutlined />}
                     title="Video Library"
                     description="Short walkthroughs of KAPsurvey features"
                     meta={
                        hasVideos ? (
                           <>
                              <span className="help-center__count">{videos.length}</span>
                              <RightOutlined />
                           </>
                        ) : (
                           <span className="help-center__tag">Coming soon</span>
                        )
                     }
                     disabled={!hasVideos}
                     onClick={hasVideos ? () => onPush({ view: "videos" }) : undefined}
                  />
                  <HelpItem
                     icon={<ReadOutlined />}
                     title="User Guide"
                     description="Step-by-step articles about every feature"
                     meta={
                        <>
                           <span className="help-center__count">{articles.length}</span>
                           <RightOutlined />
                        </>
                     }
                     onClick={() => onPush({ view: "guide" })}
                  />
                  {/* Not built yet: shown so users know it is on the way. */}
                  <HelpItem
                     icon={<CompassOutlined />}
                     title="Product Tour"
                     description="A guided walk through the platform"
                     meta={<span className="help-center__tag">Coming soon</span>}
                     disabled
                  />
                  <HelpItem
                     icon={<VideoCameraOutlined />}
                     title="Live Training"
                     description="Join our upcoming webinars and events"
                     meta={<ExportOutlined />}
                     href={LIVE_TRAINING_URL}
                     target="_blank"
                     rel="noopener noreferrer"
                  />
               </section>

               <section className="help-center__section">
                  <h5 className="help-center__section-title">Get help</h5>
                  <HelpItem
                     icon={<SparkleIcon size={20} />}
                     title="Ask AI"
                     description="Get an answer about KAPsurvey in seconds"
                     meta={<RightOutlined />}
                     onClick={() => onPush({ view: "ask" })}
                  />
                  {canOpenTickets && (
                     <HelpItem
                        icon={<CustomerServiceOutlined />}
                        title="Contact support"
                        highlighted
                        description="Report a problem or ask our team a question"
                        meta={
                           <>
                              {recentTickets?.total > 0 && (
                                 <span className="help-center__count">
                                    {recentTickets.total}
                                 </span>
                              )}
                              <RightOutlined />
                           </>
                        }
                        to="/tickets"
                        onClick={onClose}
                     />
                  )}
               </section>

               <p className="help-center__shortcut">
                  Press <kbd>?</kbd> to open Help from any page.
               </p>
            </>
         )}

         <VideoPlayer video={playing} onClose={() => setPlaying(null)} />
      </>
   );
};

export default HelpHome;
